import { useEffect, useRef } from "react";
import { Outlet, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@lyder-talk/core";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";

import { AuthShowcase } from "@/components/auth/AuthShowcase";
import { resolveSafeRedirect, isAbsoluteRedirect } from "@lyder-talk/core/lib/safe-redirect";
import { auth } from "@lyder-talk/core/lib/auth-client";

export const AuthLayout = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isLoading } = useAuth();
  const hasRedirected = useRef(false);

  const redirectTo = searchParams.get("redirectTo");

  useEffect(() => {
    if (isLoading) return;

    const handleAuthRedirect = (session: boolean) => {
      if (session && !hasRedirected.current) {
        const hash = window.location.hash;
        const search = window.location.search;
        const hashParams = new URLSearchParams(hash.substring(1));
        const searchParamsObj = new URLSearchParams(search);

        const type = hashParams.get("type") || searchParamsObj.get("type");
        const accessToken = hashParams.get("access_token") || searchParamsObj.get("access_token");
        const tokenHash = searchParamsObj.get("token_hash");

        // The email-link pages establish a real session before their flow is
        // finished — reset and invite still need a password, confirm still needs
        // to report the outcome — so redirecting on that session would cut them
        // off. The pathname check covers the moment after a page scrubs the
        // token out of the URL.
        const isAuthEmailPage = ["/auth/reset-password", "/auth/invite", "/auth/confirm"].some(
          (path) => window.location.pathname.startsWith(path)
        );

        if (
          type === "invite" ||
          type === "recovery" ||
          accessToken ||
          tokenHash ||
          isAuthEmailPage
        ) {
          console.log("Auth flow detected in AuthLayout, skipping redirect");
          return;
        }

        hasRedirected.current = true;
        // Only first-party destinations survive; anything else falls back to "/".
        const finalRedirect = resolveSafeRedirect(redirectTo);

        if (isAbsoluteRedirect(finalRedirect)) {
          window.location.href = finalRedirect;
        } else {
          navigate(finalRedirect, { replace: true });
        }
      }
    };

    const { data: { subscription } } = auth.onAuthStateChange((event, session) => {
      handleAuthRedirect(!!session);
    });

    auth.getSession().then(({ data: { session } }) => {
      handleAuthRedirect(!!session);
    });

    return () => subscription.unsubscribe();
  }, [isLoading, navigate, redirectTo]);

  return (
    <div className="flex min-h-screen">
      {/* Left — the form. Deliberately still: one column, one decision.

          The pane is split in two on purpose. The outer box owns the height
          and the backdrop, so the ambient colour stays put while the inner
          box scrolls. Before this, the pane was a single overflow-hidden box:
          a form taller than the viewport — signup on a short laptop — was
          clipped with no way to reach the submit button. */}
      <div className="relative w-full lg:h-screen lg:w-1/2 xl:w-[46%]">
        <AmbientBackdrop />

        <div className="relative z-10 h-full overflow-y-auto overscroll-y-contain lg:h-screen">
          {/* No `justify-center` here either — see the note on the showcase
              panel. `min-h-full` keeps the free space positive, and `my-auto`
              does the centring. */}
          <div className="flex min-h-full flex-col px-4 py-8 sm:px-8 sm:py-10 lg:px-12 lg:py-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              /* `my-auto` instead of `justify-center` alone: a centred flex
                 child that overflows gets pinned out of scroll reach at the
                 top. Auto margins centre when there is room and collapse to
                 zero when there isn't. */
              className="mx-auto my-auto w-full max-w-md"
            >
              <BackLink />
              <Outlet />
            </motion.div>
          </div>
        </div>
      </div>

      {/* Right — the animated showcase. See AuthShowcase for the layering. */}
      <AuthShowcase />
    </div>
  );
};

/**
 * The two drifting colour fields behind the form. Kept deliberately faint:
 * the form is the subject, and anything stronger competes with the
 * gradient on the primary button.
 */
const AmbientBackdrop = () => (
  <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
    <div className="absolute inset-0 bg-background" />
    <div className="animate-mesh-1 absolute -right-[12%] -top-[18%] h-[380px] w-[380px] rounded-full bg-primary/10 blur-[90px] dark:bg-primary/[0.16]" />
    <div className="animate-mesh-2 absolute -left-[14%] top-[34%] h-[320px] w-[320px] rounded-full bg-secondary/10 blur-[80px] dark:bg-secondary/[0.12]" />
    <div
      className="absolute inset-0 opacity-[0.02]"
      style={{
        backgroundImage:
          "linear-gradient(hsl(var(--primary)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--primary)) 1px, transparent 1px)",
        backgroundSize: "56px 56px",
      }}
    />
  </div>
);

/** Small escape hatch back to the marketing site — present on every auth
 *  screen, so it is chrome rather than content and stays sans. */
const BackLink = () => {
  const { t } = useTranslation();
  return (
  <a
    href="/"
    className="mb-7 inline-flex items-center gap-1.5 text-[13px] font-medium text-stone-500 transition-colors hover:text-stone-900 dark:text-stone-400 dark:hover:text-stone-100"
  >
    <ArrowLeft className="h-3.5 w-3.5 transition-transform duration-200" />
    {t("auth.backToApp", "Back to Lyder Talk")}
  </a>
  );
};
